"use client"

import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react"
import { supabase } from "@/lib/supabase/client"
import type { User } from "@supabase/supabase-js"
import type { Profile } from "@/types/auth"

interface AuthContextType {
  user: User | null
  isLoading: boolean
  activeProfile: Profile | null
  profiles: Profile[]
  setActiveProfile: (profile: Profile | null) => void
  login: (email: string, password: string) => Promise<boolean>
  signup: (email: string, name: string, password: string) => Promise<boolean>
  logout: () => Promise<void>
  createProfile: (name: string, type: "parent" | "child") => Promise<Profile>
  deleteProfile: (profileId: string) => Promise<boolean>
  startLessonActivity: (profileId: string, lessonTopic: string, lessonCategory: string) => Promise<string>
  endLessonActivity: (activityId: string) => Promise<void>
  getLessonActivities: (profileId?: string) => Promise<any[]>
  incrementLessonMessages: (activityId: string) => Promise<void>
  refreshAuth: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [activeProfile, setActiveProfile] = useState<Profile | null>(null)
  const [profiles, setProfiles] = useState<Profile[]>([])
  const [authInitialized, setAuthInitialized] = useState(false)
  const [authError, setAuthError] = useState<Error | null>(null)

  // Load profiles for a user
  const loadProfiles = useCallback(
    async (userId: string, userEmail?: string) => {
      console.log("[v0] loadProfiles → START", userId)

      try {
        const { data, error } = await supabase
          .from("profiles")
          .select("*")
          .eq("user_id", userId)
          .order("created_at", { ascending: true })

        if (error) {
          console.error("[v0] loadProfiles → SELECT error:", error)
          return false
        }

        console.log("[v0] loadProfiles → SELECT result:", data)

        if (data && data.length > 0) {
          const formattedProfiles = data.map((p) => ({
            id: p.id,
            name: p.name,
            type: p.type,
            userId: p.user_id,
          }))

          setProfiles(formattedProfiles)
          if (!activeProfile) {
            const parentProfile = formattedProfiles.find((p) => p.type === "parent")
            setActiveProfile(parentProfile || formattedProfiles[0])
          }

          console.log("[v0] loadProfiles → SUCCESS")
          return true
        }

        console.log("[v0] loadProfiles → No profiles found, creating default profile")

        const defaultName = userEmail ? userEmail.split("@")[0] + "'s Profile" : "My Profile"

        const { data: newProfileData, error: insertError } = await supabase
          .from("profiles")
          .insert({
            user_id: userId,
            name: defaultName,
            type: "parent",
          })
          .select()
          .single()

        if (insertError) {
          console.error("[v0] loadProfiles → Failed to create default profile:", insertError)
          setProfiles([])
          setActiveProfile(null)
          return false
        }

        const newProfile: Profile = {
          id: newProfileData.id,
          name: newProfileData.name,
          type: newProfileData.type as "parent" | "child",
          userId: newProfileData.user_id,
        }

        setProfiles([newProfile])
        setActiveProfile(newProfile)
        console.log("[v0] loadProfiles → Default profile created successfully")
        return true
      } catch (error) {
        console.error("[v0] loadProfiles → UNHANDLED error:", error)
        return false
      }
    },
    [activeProfile],
  )

  // Function to refresh auth state - can be called manually
  const refreshAuth = useCallback(async () => {
    try {
      setIsLoading(true)
      setAuthError(null)
      console.log("[v0] Refreshing auth state...")

      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession()

      if (sessionError) {
        console.error("[v0] Error getting session:", sessionError)
        setAuthError(sessionError)
        setIsLoading(false)
        return
      }

      if (session?.user) {
        console.log("[v0] Session found during refresh, user:", session.user.email)
        setUser(session.user)

        const profilesLoaded = await loadProfiles(session.user.id, session.user.email)

        if (!profilesLoaded) {
          console.warn("[v0] Failed to load profiles during refresh")
        }
      } else {
        console.log("[v0] No session found during refresh")
        setUser(null)
        setProfiles([])
        setActiveProfile(null)
      }
    } catch (error) {
      console.error("[v0] Error refreshing auth:", error)
      setAuthError(error instanceof Error ? error : new Error("Unknown error refreshing auth"))
    } finally {
      setIsLoading(false)
      setAuthInitialized(true)
    }
  }, [loadProfiles])

  useEffect(() => {
    let mounted = true
    let hasLoadedInitialUser = false

    const maybeLoadProfiles = async (user: User) => {
      if (!mounted) return
      try {
        console.log("[v0] Loading profiles for user:", user.id)
        await loadProfiles(user.id, user.email)
      } catch (err) {
        console.error("[v0] Failed to load profiles:", err)
        if (mounted) {
          setAuthError(err instanceof Error ? err : new Error("Unknown profile load error"))
        }
      }
    }

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log("[v0] Auth state changed:", event)

      if (session?.user && mounted) {
        if (event === "SIGNED_IN") {
          console.log("[v0] User authenticated1:", session.user.email)
          setUser(session.user)

          setTimeout(async () => {
            if (mounted) {
              await maybeLoadProfiles(session.user)
            }
          }, 200)
        } else {
          console.log("[v0] User authenticated2:", session.user.email)
          setUser(session.user)

          if (!hasLoadedInitialUser) {
            await maybeLoadProfiles(session.user)
            hasLoadedInitialUser = true
          }
        }
      } else if (mounted) {
        console.log("[v0] User signed out")
        setUser(null)
        setProfiles([])
        setActiveProfile(null)
      }

      if (mounted) {
        setAuthInitialized(true)
        setIsLoading(false)
      }
    })

    return () => {
      mounted = false
      subscription.unsubscribe()
    }
  }, [loadProfiles])

  // Ensure loading state is eventually turned off
  useEffect(() => {
    // Safety timeout to prevent infinite loading
    const timeout = setTimeout(() => {
      if (isLoading) {
        console.warn("Auth loading timeout reached, forcing loading state to false")
        setIsLoading(false)
      }
    }, 10000) // 10 seconds timeout

    return () => clearTimeout(timeout)
  }, [isLoading])

  // Login with email and password
  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      setIsLoading(true)
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })

      if (error) {
        console.error("Login error:", error)
        setAuthError(error)
        setIsLoading(false)
        throw error
      }

      return !!data.user
    } catch (error) {
      console.error("Login error:", error)
      setIsLoading(false)
      return false
    }
  }

  // Sign up with email and password
  const signup = async (email: string, name: string, password: string): Promise<boolean> => {
    try {
      setIsLoading(true)
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
      })

      if (error) {
        console.error("Signup error:", error)
        setAuthError(error)
        setIsLoading(false)
        throw error
      }

      if (data.user) {
        // Create a parent profile for the new user
        await supabase.from("profiles").insert({
          user_id: data.user.id,
          name: `${name}'s Profile`,
          type: "parent",
        })

        return true
      }

      setIsLoading(false)
      return false
    } catch (error) {
      console.error("Signup error:", error)
      setIsLoading(false)
      return false
    }
  }

  // Logout
  const logout = async (): Promise<void> => {
    try {
      setIsLoading(true)
      await supabase.auth.signOut()
      setUser(null)
      setProfiles([])
      setActiveProfile(null)
      setIsLoading(false)
    } catch (error) {
      console.error("Logout error:", error)
      setIsLoading(false)
    }
  }

  // Create a new profile
  const createProfile = async (name: string, type: "parent" | "child"): Promise<Profile> => {
    if (!user) {
      throw new Error("User not authenticated")
    }

    try {
      const { data, error } = await supabase
        .from("profiles")
        .insert({
          user_id: user.id,
          name,
          type,
        })
        .select()
        .single()

      if (error) {
        throw error
      }

      const newProfile: Profile = {
        id: data.id,
        name: data.name,
        type: data.type as "parent" | "child",
        userId: data.user_id,
      }

      setProfiles((prev) => [...prev, newProfile])
      return newProfile
    } catch (error) {
      console.error("Error creating profile:", error)
      throw error
    }
  }

  // Delete a profile
  const deleteProfile = async (profileId: string): Promise<boolean> => {
    if (!user) {
      return false
    }

    try {
      // Don't allow deleting the parent profile
      const profileToDelete = profiles.find((p) => p.id === profileId)
      if (!profileToDelete || profileToDelete.type === "parent") {
        return false
      }

      const { error } = await supabase.from("profiles").delete().eq("id", profileId).eq("user_id", user.id)

      if (error) {
        throw error
      }

      // Update profiles state
      setProfiles((prev) => prev.filter((p) => p.id !== profileId))

      // If the active profile is being deleted, switch to the parent profile
      if (activeProfile && activeProfile.id === profileId) {
        const parentProfile = profiles.find((p) => p.type === "parent")
        setActiveProfile(parentProfile || null)
      }

      return true
    } catch (error) {
      console.error("Error deleting profile:", error)
      return false
    }
  }

  // Start a lesson activity
  const startLessonActivity = async (
    profileId: string,
    lessonTopic: string,
    lessonCategory: string,
  ): Promise<string> => {
    try {
      const { data, error } = await supabase
        .from("activities")
        .insert({
          profile_id: profileId,
          lesson_topic: lessonTopic,
          lesson_category: lessonCategory,
          start_time: new Date().toISOString(),
          messages: 1,
        })
        .select()
        .single()

      if (error) {
        throw error
      }

      return data.id
    } catch (error) {
      console.error("Error starting lesson activity:", error)
      throw error
    }
  }

  // End a lesson activity
  const endLessonActivity = async (activityId: string): Promise<void> => {
    try {
      const now = new Date().toISOString()

      // Get the activity to calculate duration
      const { data: activity, error: fetchError } = await supabase
        .from("activities")
        .select("start_time")
        .eq("id", activityId)
        .single()

      if (fetchError) {
        throw fetchError
      }

      // Calculate duration in seconds
      const startTime = new Date(activity.start_time).getTime()
      const endTime = new Date(now).getTime()
      const durationInSeconds = Math.floor((endTime - startTime) / 1000)

      const { error } = await supabase
        .from("activities")
        .update({
          end_time: now,
          duration: durationInSeconds,
        })
        .eq("id", activityId)

      if (error) {
        throw error
      }
    } catch (error) {
      console.error("Error ending lesson activity:", error)
    }
  }

  // Increment message count for an activity
  const incrementLessonMessages = async (activityId: string): Promise<void> => {
    try {
      const { error } = await supabase.rpc("increment_activity_messages", {
        activity_id: activityId,
      })

      if (error) {
        // Fallback if RPC function doesn't exist
        const { data: activity, error: fetchError } = await supabase
          .from("activities")
          .select("messages")
          .eq("id", activityId)
          .single()

        if (fetchError) throw fetchError

        const { error: updateError } = await supabase
          .from("activities")
          .update({ messages: activity.messages + 1 })
          .eq("id", activityId)

        if (updateError) throw updateError
      }
    } catch (error) {
      console.error("Error incrementing lesson messages:", error)
    }
  }

  // Get lesson activities
  const getLessonActivities = async (profileId?: string): Promise<any[]> => {
    try {
      let query = supabase
        .from("activities")
        .select(`
          *,
          profiles:profile_id (name, type)
        `)
        .order("start_time", { ascending: false })

      if (profileId) {
        query = query.eq("profile_id", profileId)
      }
      console.log("Starting query from Lesson Activities")
      const { data, error } = await query
      console.log("Finished query from Lesson Activities" + data)

      if (error) {
        throw error
      }

      return data || []
    } catch (error) {
      console.error("Error getting lesson activities:", error)
      return []
    }
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        activeProfile,
        profiles,
        setActiveProfile,
        login,
        signup,
        logout,
        createProfile,
        deleteProfile,
        startLessonActivity,
        endLessonActivity,
        getLessonActivities,
        incrementLessonMessages,
        refreshAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}
